import React from "react";
import "@/App.css";
import "@/index.css";
import "@/team-packages.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LanguageProvider } from "@/lib/i18n";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import ProductTour from "@/components/ProductTour";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Plans from "@/pages/Plans";
import NewPlan from "@/pages/NewPlan";
import PlanDetail from "@/pages/PlanDetail";
import Products from "@/pages/Products";
import PalletTypes from "@/pages/PalletTypes";
import Recipes from "@/pages/Recipes";
import TestCenter from "@/pages/TestCenter";
import Reports from "@/pages/Reports";
import CommercialInvoice from "@/pages/CommercialInvoice";
import CommercialInvoiceDesigner from "@/pages/CommercialInvoiceDesigner";
import PackingListDesigner from "@/pages/PackingListDesigner";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import AdminUsers from "@/pages/AdminUsers";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Billing from "@/pages/Billing";
import LegalPage from "@/pages/LegalPage";
import Onboarding from "@/pages/Onboarding";
import TrashPage from "@/pages/Trash";
import AccountData from "@/pages/AccountData";
import AdminCustomers from "@/pages/AdminCustomers";
import AdminPaymentMethods from "@/pages/AdminPaymentMethods";
import GrowthCenter from "@/pages/GrowthCenter";
import EducationCenter from "@/pages/EducationCenter";
import WarehouseExecution from "@/pages/WarehouseExecution";
import AuditLog from "@/pages/AuditLog";
import DeveloperIntegrations from "@/pages/DeveloperIntegrations";
import DocumentMailer from "@/pages/DocumentMailer";
import Teams from "@/pages/Teams";
import TeamInviteAccept from "@/pages/TeamInviteAccept";
import SupportCenter from "@/pages/SupportCenter";
import AdminSupport from "@/pages/AdminSupport";
import NotFound from "@/pages/NotFound";

const ONBOARDING_EXEMPT = new Set(["/onboarding", "/products", "/pallet-types", "/settings", "/profile", "/billing", "/trash", "/account-data", "/teams", "/support"]);
const menuKeyForPath=(path)=>{
  if(path==="/app"||path==="/")return "dashboard";
  if(path.startsWith("/plans/new"))return "new_plan";
  if(path.startsWith("/plans")||path.startsWith("/packing-list"))return "plans";
  if(path.startsWith("/invoices"))return "invoices";
  if(path.startsWith("/products"))return "products";
  if(path.startsWith("/pallet-types"))return "pallet_types";
  if(path.startsWith("/recipes"))return "recipes";
  if(path.startsWith("/test-center"))return "test_center";
  if(path.startsWith("/reports"))return "reports";
  if(path.startsWith("/settings"))return "settings";
  if(path.startsWith("/trash"))return "trash";
  if(path.startsWith("/growth"))return "growth";
  if(path.startsWith("/education"))return "education";
  if(path.startsWith("/warehouse"))return "warehouse";
  if(path.startsWith("/audit"))return "activity";
  if(path.startsWith("/developer"))return "developer";
  if(path.startsWith("/document-mail"))return "document_mail";
  if(path.startsWith("/support"))return "support";
  return null;
};

function LoadingScreen({ text = "Yükleniyor…" }) {
  return <div className="min-h-[55vh] p-6 flex items-center justify-center"><div className="bg-white border border-slate-200 px-6 py-5 text-center shadow-sm"><div className="w-7 h-7 mx-auto border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin"/><div className="mt-3 text-sm font-semibold text-slate-600">{text}</div></div></div>;
}

function Guard({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  const [onboarding, setOnboarding] = React.useState(() => sessionStorage.getItem("h3poe_onboarding_complete") === "1" ? true : null);
  const [allowedMenus,setAllowedMenus]=React.useState(null);
  React.useEffect(() => {
    if (!user || onboarding === true || location.pathname === "/onboarding") return;
    let alive = true;
    api.get("/onboarding/status").then((r) => {
      if (!alive) return;
      const done = !!r.data?.completed;
      setOnboarding(done);
      if (done) sessionStorage.setItem("h3poe_onboarding_complete", "1");
    }).catch(() => { if (alive) setOnboarding(true); });
    return () => { alive = false; };
  }, [user, location.pathname, onboarding]);
  React.useEffect(()=>{
    if(!user?.team_owner_id){setAllowedMenus(null);return;}
    let alive=true; api.get("/team/menu-permissions").then(r=>{if(alive)setAllowedMenus(r.data?.menus||[])}).catch(()=>{if(alive)setAllowedMenus([])}); return()=>{alive=false};
  },[user?.id,user?.team_owner_id,user?.team_role]);
  if (!ready) return <LoadingScreen text="Hesabınız yükleniyor…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (onboarding === null && !ONBOARDING_EXEMPT.has(location.pathname)) return <LoadingScreen text="Hesabınız hazırlanıyor…" />;
  if (onboarding === false && !ONBOARDING_EXEMPT.has(location.pathname)) return <Navigate to="/onboarding" replace />;
  const menuKey=menuKeyForPath(location.pathname);
  if(user.team_owner_id&&menuKey&&allowedMenus===null)return <LoadingScreen text="Yetkiler kontrol ediliyor…" />;
  if(user.team_owner_id&&menuKey&&allowedMenus&&!allowedMenus.includes(menuKey))return <Navigate to="/app" replace/>;
  if (location.pathname === "/onboarding") return <AppErrorBoundary>{children}</AppErrorBoundary>;
  return <Layout><AppErrorBoundary>{children}</AppErrorBoundary><ProductTour user={user}/></Layout>;
}

function AdminGuard({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <LoadingScreen text="Yönetim paneli yükleniyor…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return <Layout><AppErrorBoundary>{children}</AppErrorBoundary></Layout>;
}

function RootRoute() {
  const { user, ready } = useAuth();
  if (!ready) return <LoadingScreen />;
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/team-invite" element={<TeamInviteAccept />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/privacy" element={<LegalPage type="privacy" />} />
      <Route path="/kvkk" element={<LegalPage type="kvkk" />} />
      <Route path="/terms" element={<LegalPage type="terms" />} />
      <Route path="/cookies" element={<LegalPage type="cookies" />} />
      <Route path="/cancellation-refund" element={<LegalPage type="cancellation-refund" />} />
      <Route path="/" element={<RootRoute />} />
      <Route path="/onboarding" element={<Guard><Onboarding /></Guard>} />
      <Route path="/app" element={<Guard><Dashboard /></Guard>} />
      <Route path="/plans" element={<Guard><Plans /></Guard>} />
      <Route path="/plans/new" element={<Guard><NewPlan /></Guard>} />
      <Route path="/plans/:id/edit" element={<Guard><NewPlan mode="edit" /></Guard>} />
      <Route path="/plans/:id" element={<Guard><PlanDetail /></Guard>} />
      <Route path="/packing-list/designer" element={<Guard><PackingListDesigner /></Guard>} />
      <Route path="/products" element={<Guard><Products /></Guard>} />
      <Route path="/pallet-types" element={<Guard><PalletTypes /></Guard>} />
      <Route path="/recipes" element={<Guard><Recipes /></Guard>} />
      <Route path="/invoices" element={<Guard><CommercialInvoice /></Guard>} />
      <Route path="/invoices/designer" element={<Guard><CommercialInvoiceDesigner /></Guard>} />
      <Route path="/invoices/:id" element={<Guard><InvoiceDetail /></Guard>} />
      <Route path="/test-center" element={<Guard><TestCenter /></Guard>} />
      <Route path="/reports" element={<Guard><Reports /></Guard>} />
      <Route path="/settings" element={<Guard><Settings /></Guard>} />
      <Route path="/profile" element={<Guard><Profile /></Guard>} />
      <Route path="/billing" element={<Guard><Billing /></Guard>} />
      <Route path="/trash" element={<Guard><TrashPage /></Guard>} />
      <Route path="/account-data" element={<Guard><AccountData /></Guard>} />
      <Route path="/teams" element={<Guard><Teams /></Guard>} />
      <Route path="/growth" element={<Guard><GrowthCenter /></Guard>} />
      <Route path="/education" element={<Guard><EducationCenter /></Guard>} />
      <Route path="/warehouse" element={<Guard><WarehouseExecution /></Guard>} />
      <Route path="/audit" element={<Guard><AuditLog /></Guard>} />
      <Route path="/developer" element={<Guard><DeveloperIntegrations /></Guard>} />
      <Route path="/document-mail" element={<Guard><DocumentMailer /></Guard>} />
      <Route path="/support" element={<Guard><SupportCenter /></Guard>} />
      <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
      <Route path="/admin/customers" element={<AdminGuard><AdminCustomers /></AdminGuard>} />
      <Route path="/admin/payment-methods" element={<AdminGuard><AdminPaymentMethods /></AdminGuard>} />
      <Route path="/admin/support" element={<AdminGuard><AdminSupport /></AdminGuard>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return <LanguageProvider><AuthProvider><BrowserRouter><Toaster position="top-right" richColors /><AppRouter /></BrowserRouter></AuthProvider></LanguageProvider>;
}

export default App;
